import { Clock3, FileText, FolderTree, GitBranch, Link2, SquareDashed, User } from "lucide-react";
import {
  createContext,
  type ElementType,
  type PropsWithChildren,
  useContext,
  useMemo,
} from "react";
import type { ReferenceKind, ReferenceTargetKind } from "@/components/ui/reference-chip";
import type { MessageMentionVersionIntent } from "@/lib/message-mention-link";

export type ReferencePresentationInput = {
  kind: ReferenceKind;
  mentionType?: string;
  targetKind?: ReferenceTargetKind;
  entityTypeKey?: string;
  iconKey?: string | null;
  href?: string;
};

export type ReferencePresentation = {
  Icon: ElementType | null;
  iconKey?: string | null;
};

export type ReferencePresentationResolver = (
  input: ReferencePresentationInput
) => ReferencePresentation;

interface ReferencePresentationProviderProps extends PropsWithChildren {
  resolver: ReferencePresentationResolver;
}

const ReferencePresentationContext = createContext<ReferencePresentationResolver | null>(null);

export function normalizeReferenceTargetKind(value: unknown): ReferenceTargetKind | null {
  if (value === "folder" || value === "location") return "location";
  if (value === "entity" || value === "version" || value === "diff") return value;
  return null;
}

/**
 * A mention that carries a version facet references a saved version or a comparison rather
 * than the element as it is now, and reads as one: the facet decides the icon, exactly as it
 * does for a link. Same vocabulary in both directions — `preview` is the `version` target.
 */
export function referenceTargetKindFromVersionIntent(
  intent: MessageMentionVersionIntent | null | undefined
): ReferenceTargetKind | undefined {
  if (!intent) return undefined;
  return intent.kind === "preview" ? "version" : "diff";
}

/** Icon for a version-faceted reference of any kind, or `null` when the input carries no facet. */
function resolveVersionFacetIcon(targetKind: ReferenceTargetKind | null | undefined) {
  if (targetKind === "version") return Clock3;
  if (targetKind === "diff") return GitBranch;
  return null;
}

/**
 * Icons for the mention types that are not framework entity types: people, uploaded
 * documents, and folders. Folders are structural tree nodes (`frameworkFolderNodes`), never
 * `frameworkEntityTypes` entries, so an entity-display lookup can only miss for them
 * (LAT-960) — they take the location icon, matching a `link` reference with
 * `targetKind: "location"`. Both this module's default resolver and the project resolver
 * consult this one mapping so the two cannot drift.
 */
export function resolveWellKnownMentionIcon(mentionType: string | undefined): ElementType | null {
  if (mentionType === "user") return User;
  if (mentionType === "uploadedDocument") return FileText;
  if (mentionType === "folder") return FolderTree;
  return null;
}

export function defaultReferencePresentationResolver(
  input: ReferencePresentationInput
): ReferencePresentation {
  const facetIcon = resolveVersionFacetIcon(input.targetKind);
  if (facetIcon) {
    return { Icon: facetIcon };
  }

  if (input.kind === "link") {
    return { Icon: input.targetKind === "location" ? FolderTree : Link2 };
  }

  return { Icon: resolveWellKnownMentionIcon(input.mentionType) ?? SquareDashed };
}

export function ReferencePresentationProvider({
  resolver,
  children,
}: ReferencePresentationProviderProps) {
  const value = useMemo(() => resolver, [resolver]);
  return (
    <ReferencePresentationContext.Provider value={value}>
      {children}
    </ReferencePresentationContext.Provider>
  );
}

export function useReferencePresentationResolver(): ReferencePresentationResolver {
  return useContext(ReferencePresentationContext) ?? defaultReferencePresentationResolver;
}

export function useReferencePresentation(input: ReferencePresentationInput): ReferencePresentation {
  return useReferencePresentationResolver()(input);
}
